import * as React from 'react'
import { PenLine, Trash2 } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useQuery } from '@/hooks/use-query'
import type { CommonGoal, TeamGoal } from '@/lib/types'
import { GOAL_ICONS, goalIcon } from '@/lib/goal-icons'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useDirty, useDiscardGuard } from '@/hooks/use-unsaved'
import { DiscardPrompt } from '@/components/discard-prompt'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

/**
 * Add, edit or delete one goal. A dialog rather than an inline form because the
 * dashboard's goal cards are read far more often than they're written — the
 * reading view shouldn't carry a form's worth of chrome.
 */
export function GoalEditor({
  teamId,
  goal,
  /** Existing goals, so a new one lands at the end of the lead's order. */
  siblings,
  onClose,
  onSaved,
}: {
  teamId: string
  goal?: TeamGoal
  siblings: TeamGoal[]
  onClose: () => void
  onSaved: () => void
}) {
  const [title, setTitle] = React.useState(goal?.title ?? '')
  const [description, setDescription] = React.useState(goal?.description ?? '')
  // A goal always ends up with an icon: the cards read better when none of them
  // is the odd one out, and the first entry is the generic one.
  const [icon, setIcon] = React.useState(goal?.icon ?? GOAL_ICONS[0].name)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  // Which catalog goal this is, if any. Preserved on edit so rewording a goal
  // picked from the catalog doesn't quietly cut it loose from its topics.
  const [commonGoalId, setCommonGoalId] = React.useState<string | null>(
    goal?.common_goal_id ?? null,
  )

  const dirty = useDirty({ title, description, icon, commonGoalId })
  const guard = useDiscardGuard(dirty, onClose)

  // ---- Suggestions -------------------------------------------------------
  //
  // Offered, never imposed: the free-text fields below are the same fields
  // whether or not anything here is picked, and a lead who has their own goal in
  // mind can ignore this entirely. Picking one only PREFILLS those fields — the
  // wording becomes the team's the moment it is saved, which is why editing the
  // catalog later can't rewrite it (see migrations/0027_common_goals.sql).
  //
  // Only offered on a NEW goal. On an edit the fields already hold the team's own
  // words, and a picker that overwrites them is a foot-gun rather than a
  // shortcut.
  const suggestions = useQuery(async () => {
    if (goal) return { goals: [] as CommonGoal[], counts: new Map<string, number>() }
    const [cat, links] = await Promise.all([
      sb().from('common_goals').select('*').eq('active', true).order('display_order'),
      sb().from('common_goal_presentations').select('goal_id'),
    ])
    if (cat.error) throw cat.error
    if (links.error) throw links.error
    const counts = new Map<string, number>()
    for (const l of (links.data ?? []) as { goal_id: string }[]) {
      counts.set(l.goal_id, (counts.get(l.goal_id) ?? 0) + 1)
    }
    return { goals: (cat.data ?? []) as CommonGoal[], counts }
  }, [Boolean(goal)])

  // A goal the team already has is not a suggestion — the unique index in 0027
  // would refuse the insert anyway, and offering it is offering an error.
  const taken = new Set(siblings.map((s) => s.common_goal_id).filter(Boolean) as string[])
  const offered = (suggestions.data?.goals ?? []).filter((c) => !taken.has(c.id))

  function pick(c: CommonGoal) {
    setCommonGoalId(c.id)
    setTitle(c.title)
    setDescription(c.description ?? '')
    if (c.icon) setIcon(c.icon)
    setError(null)
  }

  function writeOwn() {
    setCommonGoalId(null)
    setTitle('')
    setDescription('')
    setIcon(GOAL_ICONS[0].name)
  }

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) return setError('Give the goal a title.')

    setBusy(true)
    setError(null)
    try {
      const row = {
        title: title.trim(),
        description: description.trim() || null,
        icon,
        common_goal_id: commonGoalId,
      }
      const { error: err } = goal
        ? await sb().from('team_goals').update(row).eq('id', goal.id)
        : await sb().from('team_goals').insert({
            ...row,
            team_id: teamId,
            // Spaced by 10, so a later goal can be slotted between two others.
            display_order: Math.max(0, ...siblings.map((g) => g.display_order)) + 10,
          })
      if (err) throw err
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!goal) return
    if (!window.confirm(`Delete “${goal.title}”? This can't be undone.`)) return
    setBusy(true)
    const { error: err } = await sb().from('team_goals').delete().eq('id', goal.id)
    setBusy(false)
    if (err) return setError(err.message)
    onSaved()
  }

  return (
    <Dialog open onOpenChange={(o) => !o && guard.requestClose()}>
      <DialogContent className="sm:max-w-lg">
        <DiscardPrompt guard={guard} what="this goal" />
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle>{goal ? 'Edit goal' : 'New goal'}</DialogTitle>
            <DialogDescription>
              What the team is trying to achieve. Shown at the top of the dashboard for everyone.
            </DialogDescription>
          </DialogHeader>

          <div className="my-4 space-y-4">
            {/* Suggestions first, then the fields they fill in — the order someone
                works through it. Absent entirely on an edit, and absent when the
                team has already picked every catalog goal there is. */}
            {!goal && offered.length > 0 && (
              <div className="space-y-2">
                <Label id="goal-common-label">Start from a common goal (optional)</Label>
                <div
                  role="radiogroup"
                  aria-labelledby="goal-common-label"
                  className="max-h-[30vh] space-y-1 overflow-y-auto rounded-md border p-2"
                >
                  {offered.map((c) => {
                    const Icon = goalIcon(c.icon)
                    const on = commonGoalId === c.id
                    const topics = suggestions.data?.counts.get(c.id) ?? 0
                    return (
                      <button
                        key={c.id}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        onClick={() => pick(c)}
                        className={cn(
                          'flex w-full items-start gap-2.5 rounded px-2 py-1.5 text-left transition-colors',
                          on ? 'bg-primary/5 ring-1 ring-primary/30' : 'hover:bg-accent',
                        )}
                      >
                        <Icon
                          className={cn(
                            'mt-0.5 h-4 w-4 shrink-0',
                            on ? 'text-primary' : 'text-muted-foreground',
                          )}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium">{c.title}</span>
                          {/* The count is what makes this a real choice rather
                              than a list of slogans: it says how much material
                              the accelerator already has behind the goal. */}
                          {topics > 0 && (
                            <span className="block text-xs text-muted-foreground">
                              {topics === 1 ? '1 topic supports this' : `${topics} topics support this`}
                            </span>
                          )}
                        </span>
                      </button>
                    )
                  })}
                </div>
                <button
                  type="button"
                  onClick={writeOwn}
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground underline-offset-2 hover:underline"
                >
                  <PenLine className="h-3 w-3" />
                  {commonGoalId ? 'Clear and write my own instead' : 'Or just write your own below'}
                </button>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="goal-title">Title</Label>
              <Input
                id="goal-title"
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ship without a release weekend"
              />
            </div>
            <div className="space-y-2">
              {/* A radiogroup rather than a Select: the icons are the labels, and
                  twenty of them fit in the space one dropdown would occupy. */}
              <Label id="goal-icon-label">Icon</Label>
              <div
                role="radiogroup"
                aria-labelledby="goal-icon-label"
                className="flex flex-wrap gap-1.5"
              >
                {GOAL_ICONS.map(({ name, label, icon: Icon }) => {
                  const on = icon === name
                  return (
                    <button
                      key={name}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      aria-label={label}
                      title={label}
                      onClick={() => setIcon(name)}
                      className={cn(
                        'flex h-9 w-9 items-center justify-center rounded-md border transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        on
                          ? 'border-primary bg-primary/10 text-foreground'
                          : 'border-input text-muted-foreground hover:bg-accent',
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="goal-description">Description (optional)</Label>
              <Textarea
                id="goal-description"
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What it means in practice, and how the team will know it happened"
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <DialogFooter className="sm:justify-between">
            {goal ? (
              <Button
                type="button"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => void remove()}
                disabled={busy}
              >
                <Trash2 /> Delete
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={guard.requestClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {goal ? 'Save changes' : 'Add goal'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
